import { cloneDeep, concat, uniq } from 'lodash';
import { v4 as uuid } from 'uuid';

import { ChannelSubtype, type DisplaySettings, type EPGData, type LinkedAssetInfo } from '@/models/channel';

import type { ISupportedVariantList } from '../../../constants/supportedVariants';
import { AdBreakType, ChannelType, LiveJoinInProgress } from '../../../enums';
import type { Category, Channel, ChannelLiveProgram, EpgEvent, GroupedAdTags, Item, TranscodeVariant } from '../../../models';
import type { ChannelRefreshControl, OU, WorkflowInfo } from '../../../models/Ou';
import type { Permissions } from '../../../models/Permissions';
import { ValidationErrors } from '../../../models/ValidationErrors';
import type { ChannelFormData } from '../../../pages/channelmanager/channels/channel-editor/ChannelFormData';
import { FuelEntityType } from '../../../services';
import { AssetLookup } from '../../../utils/assetLookup';
import calendarUtils from '../../../utils/calendarUtils';
import clientUtils from '../../../utils/clientUtils';
import itemUtils from '../../../utils/itemUtils';
import { AdsSponsorsForm } from './AdsSponsorsForm';
import type { ChannelTypeOption } from './channelTypeOptions';
import { channelTypeOptions } from './channelTypeOptions';
import { DynamicLodContentForm } from './DynamicLodContentForm';
import { EpgConfigData } from './EPGConfigData';
import { EpgConfigForm } from './EPGConfigForm';
import { GeoFencingForm } from './GeoFencingForm';
import { LiveTransitionsForm } from './LiveTransitionsForm';
import { LodTransitionsForm } from './LodTransitionsForm';
import { ProgrammedLodContentForm } from './ProgrammedLodContentForm';

const GLOBAL_FILLER_DESCRIPTION_TEXT =
  'The Global Interstitials channel allows users to set fallback Ad Break Fillers, Live Interstitials, LOD Transitions, and fallback LOD Content for use on all channels within FUEL.';

export class ChannelForm {
  mode: 'new' | 'existing' = 'new';
  // permissions: ChannelFormPermissions = new ChannelFormPermissions(new Permissions([]));
  errors: ValidationErrors = new ValidationErrors([]);
  assets: AssetLookup = new AssetLookup([]);

  id: string | null = null;
  name: string = '';
  alias: string = '';
  enableAliasFeature: boolean = false;
  type: ChannelTypeOption = channelTypeOptions.find((o) => o.id === ChannelType.Dynamic);
  url: string = '';

  // doesn't show up in the ui but is part of the channel's data...
  ouId: string = null;
  timezone: string = 'America/Los_Angeles';

  showAds: boolean = false;
  liveScheduleEnabled: boolean = false;
  enableEPGGeneration: boolean = false;
  isBroadcastChannel: boolean = false;
  isLinearChannel: boolean = false;

  showGeoFencing: boolean = false;
  isNestedChannelsEnabled: boolean = false;
  enabled: boolean = false;
  deleted: boolean = false;
  halted: boolean = false;
  enableExternalSubtitle: boolean = true;
  maxClipsPerChannel: number = 100;
  existingPrefixSortOrder: string[] = [];

  liveJoinInProgress: LiveJoinInProgress = LiveJoinInProgress.JoinFromBeginning;

  streamingEngineId: string = '';

  liveScheduleProgramIds: string[] = [];
  liveSchedulePrograms: Array<ChannelLiveProgram> = [];

  // prioritizeUnwatchedContent & mrssFeedUrl show up on both of the LOD content pages, so its convenient to just stick them at top-level...
  prioritizeUnwatchedContent: boolean = false;
  publicStorageDirectory: string = '';
  privateStorageDirectory: string = '';
  // this doesn't exist in the backend, and doesn't exist until a channel has been created for the 1st time (requires a channel id)...
  mrssFeedUrl: string = '';
  mrssFeedEpgUrl: string = '';
  targetDuration: number = 0; // 12;
  fps: string | null = null; // 30;
  defaultVariants: string[] = []; // ['600K360AWS', '3500K720AWS'];

  liveTransitionsForm: LiveTransitionsForm = new LiveTransitionsForm();
  lodTransitionsForm: LodTransitionsForm = new LodTransitionsForm();

  dynamicLodContentForm: DynamicLodContentForm = null;
  programmedLodContentForm: ProgrammedLodContentForm = new ProgrammedLodContentForm();

  adsSponsorsForm: AdsSponsorsForm = new AdsSponsorsForm();

  categoriesLookupById: Map<string, Category> = new Map();
  categoriesLookupByKey: Map<string, Category> = new Map();

  latestChannelData: Channel | null = null;

  originalEpgData: EPGData = null;
  epgConfigForm: EpgConfigForm = null;
  epgConfigData: EpgConfigData = null;
  daysToKeep: number = 7; // set to -1 not to filter epg events
  nestedAssets: Item[] = [];
  broadcastChannelContentClips: Item[] = [];

  geoFencingForm: GeoFencingForm = null;

  // Added isSaving to check nevigation state
  isSaving: boolean = false;
  thumbnailSrc: string = null;
  ingestFile: File | null = null;
  entityType: string = FuelEntityType.Channel;
  description: string = '';
  featureFlags: any = null;
  isGlobalInterstitialsChannel: boolean = false;
  clipIds: string[] = [];
  viewAdBreaks: boolean = false;
  isLinkedChannel: boolean = false;
  contentLinkedChannelId: string | null = '';
  adBreakDuration: number = 150;
  defaultChannelRefreshControls: ChannelRefreshControl | null;
  // enable-graphics-overlay-feature will control whether to show this control
  graphicsUrl: string | null = null;
  supportedVariants: ISupportedVariantList[] = [];
  allVariants: TranscodeVariant[] = [];
  includeClipMetadata: boolean = false;
  disableCC: boolean = false;

  enableWorkflowInfo: boolean = false;
  workflowInfo: WorkflowInfo = null;
  originalChannelData: Channel = null;

  // external channel specific fields
  externalUrl: string = '';
  externalEPGUrl: string = '';

  adTags: GroupedAdTags = {
    direct: { list: [], selected: null },
    house: { list: [], selected: [] },
    programmatic: { list: [], selected: null },
  };
  linkedAssets: LinkedAssetInfo[] = [];
  displaySettings: DisplaySettings = {
    favIcon: null,
    thumbnail: null,
    poster: null,
    background: null,
    logo: null,
    badge: null,
    backgroundColor: '#ffffff',
  };
  tmp = {
    itemLookup: {},
  };

  static create(
    ouData: OU,
    myPermissions: Permissions,
    categories: Category[],
    featureFlags: any,
    allVariants: TranscodeVariant[]
  ): ChannelForm {
    // console.log('ChannelForm create', { ouData });
    const form = new ChannelForm();
    form.featureFlags = featureFlags;
    form.mode = 'new';
    form.allVariants = allVariants;
    form.supportedVariants = itemUtils.getSupportedVariants(allVariants);
    if (ouData && ouData.metaData && ouData.metaData.defaultTargetDuration === 6) {
      form.targetDuration = 6;
    } else {
      form.targetDuration = 12;
    }

    if (ouData && ouData.metaData && ouData.metaData.defaultVariants && ouData.metaData.defaultVariants.length > 0) {
      form.fps = form.supportedVariants[ouData.metaData.defaultVariants[0]].FrameRate;
    } else {
      form.fps = '30';
    }

    if (ouData && ouData.metaData && ouData.metaData.defaultVariants && ouData.metaData.defaultVariants.length > 0) {
      form.defaultVariants = ouData.metaData.defaultVariants;
    }

    // form.permissions = new ChannelFormPermissions(myPermissions);
    form.loadCategoryLookups(categories);
    // fallback to user timezone only if the ouData is not present (refreshing the channels/editor/new page)
    form.ouId = ouData.ouId;
    form.timezone = ouData.timezone;
    form.dynamicLodContentForm = DynamicLodContentForm.create(form);
    form.epgConfigForm = EpgConfigForm.create(form);
    // New EPG Calendar Data
    form.epgConfigData = EpgConfigData.create(form);
    form.geoFencingForm = GeoFencingForm.create(form);

    form.enableWorkflowInfo = false;
    form.workflowInfo = {
      fields: {},
      commentFileS3Paths: [],
    };

    return form;
  }

  static createFromData(
    ouData: OU,
    data: ChannelFormData,
    myPermissions,
    featureFlags: any,
    allVariants: TranscodeVariant[]
  ): ChannelForm {
    const form = new ChannelForm();
    form.featureFlags = featureFlags;
    form.allVariants = allVariants;
    form.supportedVariants = itemUtils.getSupportedVariants(allVariants);
    form.loadData(ouData, data, myPermissions, featureFlags);
    // console.log('createFromData', { form });
    return form;
  }

  getChannelType(channel: Channel) {
    if (channel.type === ChannelType.Programmed && channel.metaData?.channelSubtype === ChannelSubtype.Collection) {
      return channelTypeOptions.find((o) => o.id === ChannelType.ProgrammedCollection);
    }
    if (channel.type === ChannelType.Dynamic && channel.metaData?.channelSubtype === ChannelSubtype.Collection) {
      return channelTypeOptions.find((o) => o.id === ChannelType.DynamicCollection);
    }
    if (
      channel.type === ChannelType.Programmed &&
      channel.metaData &&
      channel.metaData.epgConfig &&
      channel.metaData.epgConfig.enableEPGGeneration &&
      channel.metaData.isBroadcastChannel
    ) {
      return channelTypeOptions.find((o) => o.id === ChannelType.Scheduled);
    }
    return channelTypeOptions.find((o) => o.id === channel.type);
  }

  loadData(ouData: OU, data: ChannelFormData, myPermissions, featureFlags: any) {
    // console.log('loadData', { ouData, data, myPermissions, featureFlags });
    const { channel, categories, assets, globalData } = data;
    if (channel.id) this.mode = 'existing';

    this.originalEpgData = data.epgData;
    this.originalChannelData = data.channel;

    this.assets = new AssetLookup(assets);
    // this.permissions = new ChannelFormPermissions(myPermissions);
    this.latestChannelData = channel;

    this.loadCategoryLookups(categories);

    this.id = channel.id;
    this.name = channel.name;
    this.alias = channel.alias;
    this.enableAliasFeature = !clientUtils.isNullOrWhitespace(channel.alias);
    this.description = channel.description;
    this.clipIds = channel.clipIds;

    if (channel.type === ChannelType.LiveOnly) throw new Error('Channel type "LiveOnly" no longer supported');
    this.type = this.getChannelType(channel);
    if (this.type === undefined) {
      throw new Error(`Unexpected, unable to map channel type: ${channel.type} to an option`);
    }

    this.url = channel.url;

    // aalt fixes for bugfix/#5129-Channel-Page-issues-External-channel-MRSS-etc-aalt
    if (!clientUtils.isNullOrWhitespace(channel.id)) {
      // mirror backend logic
      // return fuelportalFastAPI.TrimEnd(' ', '/') + "/mrss?ChId=" + this.Id + "&maxItems=50";
      this.mrssFeedUrl = `${globalData.fuelPortalFastUrl}mrss?ChId=${channel.id}&maxItems=50`;
      this.mrssFeedEpgUrl = `${globalData.fuelPortalFastUrl}mrss?ChId=${channel.id}&maxItems=50&ContentType=epg`;
    }

    this.ouId = channel.ouId || ouData.ouId;
    this.timezone = channel.timezone || ouData.timezone;

    if (channel.id === ouData.ouId || channel.id === channel.ouId) {
      this.isGlobalInterstitialsChannel = true;
      this.description = GLOBAL_FILLER_DESCRIPTION_TEXT;
    }

    if (channel.type === ChannelType.External) {
      this.externalUrl = channel?.metaData?.externalChannelInfo?.externalUrl;
      this.externalEPGUrl = channel?.metaData?.externalChannelInfo?.externalEPGUrl;
    }

    this.showAds = channel.showAds;
    if (channel.liveScheduleEnabled !== undefined) this.liveScheduleEnabled = channel.liveScheduleEnabled;

    if (channel.enabled !== undefined) this.enabled = channel.enabled;
    if (channel.deleted !== undefined) this.deleted = channel.deleted;
    if (channel.halted !== undefined) this.halted = channel.halted;

    if (channel.metaData && channel.metaData.geoFencing) {
      if (channel.metaData.geoFencing.zipcodes && channel.metaData.geoFencing.zipcodes.length > 0) {
        this.showGeoFencing = true;
      }
    }

    if (channel.metaData && channel.metaData.nestedChannelConfig && channel.metaData.nestedChannelConfig.isEnabled) {
      this.isNestedChannelsEnabled = true;
    }

    if (channel.metaData && channel.metaData.channelContentLimits) {
      if (channel.metaData.channelContentLimits.maxClipsPerChannel)
        this.maxClipsPerChannel = channel.metaData.channelContentLimits.maxClipsPerChannel;
    }

    if (!channel.metaData?.enableExternalSubtitle) {
      this.enableExternalSubtitle = false;
    }

    if (channel.metaData?.isBroadcastChannel) {
      this.isBroadcastChannel = true;
    }

    if (channel.metaData?.isLinearChannel) {
      this.isLinearChannel = true;
    }

    if (channel.metaData?.disableCC) {
      this.disableCC = channel.metaData?.disableCC;
    }

    if (channel.metaData && channel.metaData.epgConfig) {
      this.daysToKeep = channel.metaData.epgConfig.daysToKeep ?? 7;
      if (channel.metaData.epgConfig.enableEPGGeneration) {
        this.enableEPGGeneration = true;
      }
      if (channel.metaData.epgConfig.events && channel.metaData.epgConfig.events.length > 0) {
        const ianaTimezoneName = channel.timezone;
        const { events } = channel.metaData.epgConfig;
        const newEvents: EpgEvent[] = [];
        for (let i = 0; i < events.length; i++) {
          const event = events[i];
          if (event.id == null) {
            event.id = uuid();
          }

          // aalt #391217 We convert all the dates to the current user timezone without adjusting the time
          // so it appears the same in the calendar popup regardless of timezone, and we convert back to
          // the OU timezone on the fly as soon as a date is modified with the formatDateWithTimezone
          //
          // eg.
          // const timezone = this.$store.state.channelEditor.form.timezone;
          // obj.startDateTime = clientUtils.formatDateWithTimezone(obj.startDate, timezone);
          // obj.endDateTime = clientUtils.formatDateWithTimezone(obj.endDate, timezone);
          const { startDateTime } = event;
          const startYear = +startDateTime.substring(0, 4);
          const startMonth = +startDateTime.substring(4, 6) - 1;
          const startDay = +startDateTime.substring(6, 8);
          const startHours = +startDateTime.substring(8, 10);
          const startMinutes = +startDateTime.substring(10, 12);
          const startSeconds = +startDateTime.substring(12, 14);
          event.startDate = new Date(startYear, startMonth, startDay, startHours, startMinutes, startSeconds);

          const { endDateTime } = event;
          const endYear = +endDateTime.substring(0, 4);
          const endMonth = +endDateTime.substring(4, 6) - 1;
          const endDay = +endDateTime.substring(6, 8);
          const endHours = +endDateTime.substring(8, 10);
          const endMinutes = +endDateTime.substring(10, 12);
          const endSeconds = +endDateTime.substring(12, 14);
          event.endDate = new Date(endYear, endMonth, endDay, endHours, endMinutes, endSeconds);

          // aalt #391217 we do not need this code since we do the conversion as soon as we create or modify epg event dates
          // in other words the startDateTime and endDateTime are already converted
          //
          // Do a conversion so the Epg Generation calendar date picker can correctly bind to the objects in the list
          // "20210824000000 -0700" -> "2021-08-24T07:00:00.000Z" in "America/Los_Angeles"
          // "20210824010000 -0700" -> "2021-08-24T08:00:00.000Z" in "America/Los_Angeles"
          // const startDateEpgTime = moment.parseZone(event.startDateTime, 'YYYYMMDDHHmmss ZZ').toDate();
          // const endDateEpgTime = moment.parseZone(event.endDateTime, 'YYYYMMDDHHmmss ZZ').toDate();
          // event.startDate = startDateEpgTime;
          // event.endDate = endDateEpgTime;
        }
      }
    }

    if (channel.metaData && channel.metaData.includeClipMetadata) {
      this.includeClipMetadata = channel.metaData.includeClipMetadata;
    }

    if (channel && channel.metaData && channel.metaData.thumbnail && channel.metaData.thumbnail.previewUri) {
      this.thumbnailSrc = channel.metaData.thumbnail.previewUri;
    }
    if (
      channel &&
      channel.metaData &&
      channel.metaData.linkedChannelInfo &&
      channel.metaData.linkedChannelInfo.contentLinkedChannelId
    ) {
      this.isLinkedChannel = true;
      this.contentLinkedChannelId = channel.metaData.linkedChannelInfo.contentLinkedChannelId;
    }

    if (
      channel &&
      channel.metaData &&
      channel.metaData.graphicsOverlayConfig &&
      !clientUtils.isNullOrWhitespace(channel.metaData.graphicsOverlayConfig.url)
    ) {
      this.graphicsUrl = channel.metaData.graphicsOverlayConfig.url;
    }

    // don't let this stuff become undefined if it happens to be undefined in the payload...
    if (channel.streamingEngineId !== undefined) this.streamingEngineId = channel.streamingEngineId;

    if (channel.metaData?.liveProgramConfig?.liveProgramIds) {
      this.liveScheduleProgramIds = channel.metaData.liveProgramConfig.liveProgramIds;
    } else if (channel.liveScheduleProgramIds != null) {
      this.liveScheduleProgramIds = channel.liveScheduleProgramIds;
    }
    if (channel.liveSchedulePrograms !== undefined) this.liveSchedulePrograms = channel.liveSchedulePrograms;

    // original code sets this to "Join from Beginning" if falsey...
    if (channel.liveJoinInProgress !== undefined || channel.liveJoinInProgress !== null)
      this.liveJoinInProgress = channel.liveJoinInProgress;

    const value = channel.prioritizeUnwatchedContent;
    if (value !== undefined && value !== null) this.prioritizeUnwatchedContent = value;

    this.publicStorageDirectory = channel.publicStorageDirectory;
    this.privateStorageDirectory = channel.privateStorageDirectory;

    if (channel.metaData && channel.metaData.targetDuration == 6) {
      this.targetDuration = 6;
    } else {
      this.targetDuration = 12;
    }

    if (channel.metaData && channel.metaData.variantCodes && channel.metaData.variantCodes.length > 0) {
      this.fps = this.supportedVariants[channel.metaData.variantCodes[0]]?.FrameRate;
    } else {
      this.fps = '30';
    }

    if (channel.metaData && channel.metaData.variantCodes && channel.metaData.variantCodes.length > 0) {
      this.defaultVariants = channel.metaData.variantCodes;
    } else if (this.targetDuration === 6) {
      if (this.fps === '25') {
        this.defaultVariants = ['6S600K360F25AWS', '6S3500K720F25AWS'];
      } else {
        this.defaultVariants = ['6S600K360AWS', '6S3500K720AWS'];
      }
    } else {
      this.defaultVariants = ['600K360AWS', '3500K720AWS'];
    }

    // #455665 Set up and enable channels to playout as 25fps
    // filter out any variants not in the OU allowed variants array
    if (ouData && ouData.metaData && ouData.metaData.allowedVariants && ouData.metaData.allowedVariants.length > 0) {
      this.defaultVariants = this.defaultVariants.filter((v) => ouData.metaData.allowedVariants.indexOf(v) > -1);
    }

    this.liveTransitionsForm = LiveTransitionsForm.createFromData(data, myPermissions, featureFlags, ouData);
    this.lodTransitionsForm = LodTransitionsForm.createFromData(data, myPermissions, featureFlags, ouData);

    this.dynamicLodContentForm = DynamicLodContentForm.createFromData(this, data, categories);

    this.programmedLodContentForm = ProgrammedLodContentForm.createFromData(data, myPermissions, featureFlags);

    this.adsSponsorsForm = AdsSponsorsForm.createFromData(data);
    if (this.adsSponsorsForm && this.adsSponsorsForm.adBreakType?.id === AdBreakType.DragAndDropAdBreak) {
      this.adBreakDuration = this.adsSponsorsForm.podTime;
    }

    this.epgConfigForm = EpgConfigForm.createFromData(this, data);
    // New EPG Calendar Data
    this.epgConfigData = EpgConfigData.createFromData(this, data);

    this.geoFencingForm = GeoFencingForm.createFromData(this, data);

    // recalc live program fields
    if (this.isBroadcastChannel && this.epgConfigData && this.epgConfigData.events && this.epgConfigData.events.length > 0) {
      const epgLiveProgramIds = calendarUtils.getLiveProgramIdsFromEvents(this.epgConfigData.events, this.daysToKeep);
      if (epgLiveProgramIds && epgLiveProgramIds.length > 0) {
        const existingLiveProgramIds = cloneDeep(this.liveScheduleProgramIds);
        const newLiveProgramIds = uniq(concat(existingLiveProgramIds, epgLiveProgramIds));
        this.liveScheduleEnabled = true;
        this.liveScheduleProgramIds = newLiveProgramIds;
      }
    }

    // channel refresh logic
    if (channel.metaData?.refreshControl) {
      this.defaultChannelRefreshControls = {
        minTimeBetweenRefresh: channel.metaData.refreshControl.minTimeBetweenRefresh
          ? clientUtils.secondsToTimeSpanFormat(channel.metaData.refreshControl.minTimeBetweenRefresh).seconds
          : 0,
        maxTimeBetweenRefresh: channel.metaData.refreshControl.maxTimeBetweenRefresh
          ? clientUtils.secondsToTimeSpanFormat(channel.metaData.refreshControl.maxTimeBetweenRefresh).seconds
          : 0,
      };
    }

    if (channel.metaData?.workflowInfo != null) {
      this.enableWorkflowInfo = true;
      this.workflowInfo = channel.metaData?.workflowInfo;
    } else {
      this.workflowInfo = {
        fields: {},
        commentFileS3Paths: [],
      };
    }
    if (channel.metaData?.linkedAssets != null) {
      this.linkedAssets = channel.metaData.linkedAssets;
    }
    this.displaySettings = clientUtils.populateDisplaySettings(channel.metaData?.linkedAssets, channel.metaData.displaySettings);

    assets.forEach((asset) => {
      this.tmp.itemLookup[asset.id] = asset;
    });
  }

  unscheduledPrograms(): Array<ChannelLiveProgram> {
    const toReturn = [];
    for (const program of this.liveSchedulePrograms) {
      if (!program.isDeleted && !program.isScheduledProgram) {
        toReturn.push(program);
      }
    }
    return toReturn;
  }

  private loadCategoryLookups(categories: Array<Category>) {
    for (const category of categories) {
      this.categoriesLookupById.set(category.id, category);
      this.categoriesLookupByKey.set(clientUtils.getKey(category.name), category);
    }
  }
}
